import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  FloatButton, 
  Tooltip,
  Alert,
  Modal,
  Input,
  Form,
  Select,
  Checkbox,
  Button,
  message
} from 'antd';
import { 
  ToolTwoTone,
  ShareAltOutlined, 
  DownloadOutlined, 
  MailOutlined, 
  RollbackOutlined, 
  StarOutlined,
  CheckOutlined
} from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

/**
 * HeaderActionCenter component with dynamic action loading and email popup
 */
const HeaderActionCenter = ({
  activeAgent,
  componentIds = [],
  componentTypes = {},
  sessionId,
  onActionComplete
}) => {
  // State for dynamic actions
  const [availableActions, setAvailableActions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  
  // State to track loading state for each button
  const [loadingStates, setLoadingStates] = useState({});
  
  // State for custom alert
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  
  // State for email modal
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [emailForm] = Form.useForm();
  const [currentEmailAction, setCurrentEmailAction] = useState(null);

  // Get the outcome text from the agent config
  const outcomeText = activeAgent?.outcome_text || 'Processing Results';

  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [downloadFilename, setDownloadFilename] = useState('');

  // Load available actions when component mounts or agent changes
  useEffect(() => {
    if (activeAgent?.endpoint) {
      loadAvailableActions();
    }
  }, [activeAgent?.endpoint, sessionId]);

  // Function to load available actions from /get_actions endpoint
  const loadAvailableActions = async () => {
    try {
      setLoadingActions(true);
      
      // Validate activeAgent exists
      if (!activeAgent || !activeAgent.endpoint) {
        console.error('No active agent or endpoint available');
        setAvailableActions([]);
        return;
      }
      
      // Derive API URL similar to how it's done in AgentPage.jsx
      const baseUrl = activeAgent.endpoint.split('/').slice(0, 3).join('/');
      const apiUrl = `${baseUrl}/get_actions`;
      
      console.log("Loading actions from:", apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionId}`
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load actions: ${response.status}`);
      }
      
      const actionsData = await response.json();
      console.log("Loaded actions:", actionsData);
      
      // Ensure actionsData is an array
      const actionsArray = Array.isArray(actionsData) ? actionsData : [];
      setAvailableActions(actionsArray);
      
      // Cache actions in localStorage for performance
      if (activeAgent.id) {
        const cacheKey = `agent_actions_${activeAgent.id}`;
        localStorage.setItem(cacheKey, JSON.stringify({
          timestamp: new Date().toISOString(),
          agentId: activeAgent.id,
          actions: actionsArray
        }));
      }
      
    } catch (error) {
      console.error('Error loading available actions:', error);
      
      // Try to load from cache as fallback
      if (activeAgent && activeAgent.id) {
        const cacheKey = `agent_actions_${activeAgent.id}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          try {
            const cachedData = JSON.parse(cached);
            const cachedActions = Array.isArray(cachedData.actions) ? cachedData.actions : [];
            console.log("Using cached actions:", cachedActions);
            setAvailableActions(cachedActions);
          } catch (parseError) {
            console.error('Error parsing cached actions:', parseError);
            setAvailableActions([]);
          }
        } else {
          setAvailableActions([]);
        }
      } else {
        setAvailableActions([]);
      }
    } finally {
      setLoadingActions(false);
    }
  };

  // Show custom alert
  const showAlert = (message, type = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setAlertVisible(true);
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      setAlertVisible(false);
    }, 3000);
  };

  // Function to handle button click
  const handleActionClick = async (action) => {
    if (!action || !action.endpoint) {
      showAlert('Invalid action configuration', 'error');
      return;
    }

    // Special handling for email action
    if (action.id === 'send_adhoc_email') {
      setCurrentEmailAction(action);
      setEmailModalVisible(true);
      return;
    }

    // Special handling for download action
    if (action.id === 'download_pdf') {
      await handleDownloadAction(action);
      return;
    }

    // Handle other actions directly
    await executeAction(action, {});
  };

  // Function to execute an action
  const executeAction = async (action, actionParams = {}) => {
    try {
      // Set loading state for this specific button
      setLoadingStates(prev => ({ ...prev, [action.endpoint]: true }));

      // Prepare payload
      const payload = {
        componentIds: componentIds,
        componentTypes: componentTypes,
        action: action.name || action.button_name,
        agentId: activeAgent?.id,
        sessionId: sessionId,
        ...actionParams
      };

      // Call the endpoint
      const response = await fetch(action.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Action failed with status ${response.status}`);
      }

      // Process successful response
      const data = await response.json();
      
      // Handle download actions specially
      if (action.id === 'download_pdf' && data.download_url) {
        // Create and trigger download
        const link = document.createElement('a');
        link.href = data.download_url;
        link.download = ''; // Let browser determine filename from Content-Disposition
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      // Show success message
      const actionName = action.name || action.button_name || "Action";
      showAlert(data.message || `${actionName} completed successfully`);
      
      // Call the callback if provided
      if (onActionComplete) {
        onActionComplete(action, data);
      }

      return true;
    } catch (error) {
      console.error('Error executing action:', error);
      const actionName = action.name || action.button_name || "Action";
      showAlert(`Failed to execute ${actionName}: ${error.message}`, 'error');
      return false;
    } finally {
      // Clear loading state
      setLoadingStates(prev => ({ ...prev, [action.endpoint]: false }));
    }
  };

  // Handle email form submission
  const handleEmailSubmit = async () => {
    try {
      const values = await emailForm.validateFields();
      
      // Prepare email-specific parameters
      const emailParams = {
        to_email: values.to_email,
        subject: values.subject || `Analysis Results from ${activeAgent?.name || 'AI Assistant'}`,
        custom_message: values.custom_message || '',
        attach_full_report: values.attach_full_report || false,
        attachment_format: values.attachment_format || 'pdf',
        attach_components: values.attach_components || false,
        include_components: componentIds
      };
      
      // Execute the email action
      const success = await executeAction(currentEmailAction, emailParams);
      
      if (success) {
        // Close modal and reset form
        setEmailModalVisible(false);
        emailForm.resetFields();
        setCurrentEmailAction(null);
      }
      
    } catch (error) {
      console.error('Email form validation failed:', error);
    }
  };



  // Function to handle download action
  const handleDownloadAction = async (action) => {
    try {
      setLoadingStates(prev => ({ ...prev, [action.endpoint]: true }));

      const payload = {
        componentIds: componentIds,
        componentTypes: componentTypes,
        action: action.name || action.button_name,
        agentId: activeAgent?.id,
        sessionId: sessionId
      };

      const response = await fetch(action.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionId}`
        },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Download preparation failed with status ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.download_url) {
        // Extract filename from URL
        const filename = data.download_url.split('/').pop() || 'Pre-Operative_Instructions.pdf';
        
        setDownloadUrl(data.download_url);
        setDownloadFilename(filename);
        setDownloadModalVisible(true);
      } else {
        throw new Error('Failed to generate download link');
      }

    } catch (error) {
      console.error('Error preparing download:', error);
      showAlert(`Failed to prepare download: ${error.message}`, 'error');
    } finally {
      setLoadingStates(prev => ({ ...prev, [action.endpoint]: false }));
    }
  };




  // Helper function to get the appropriate icon
  const getIconComponent = (iconName) => {
    if (!iconName) return <CheckOutlined />;
    
    const iconLower = iconName.toString().replace('.png', '').toLowerCase();
    
    switch (iconLower) {
      case 'mail':
      case 'email':
        return <MailOutlined />;
      case 'share':
        return <ShareAltOutlined />;
      case 'download':
        return <DownloadOutlined />;
      case 'back':
        return <RollbackOutlined />;
      case 'ratings':
      case 'feedback':
        return <StarOutlined />;
      case 'complete':
        return <CheckOutlined />;
      default:
        return <CheckOutlined />; // Default icon
    }
  };

  // Don't render if no active agent
  if (!activeAgent) {
    return null;
  }

  // Use dynamically loaded actions or fallback to agent config
  const actions = availableActions.length > 0 ? availableActions : (activeAgent.action_center || []);

  return (
    <>
      {/* Alert banner */}
      {alertVisible && (
        <div style={{ 
          position: 'fixed', 
          top: '20px', 
          left: '50%', 
          transform: 'translateX(-50%)',
          zIndex: 1000,
          width: '80%',
          maxWidth: '500px'
        }}>
          <Alert
            message={alertMessage}
            type={alertType}
            showIcon
            closable
            onClose={() => setAlertVisible(false)}
            style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' }}
          />
        </div>
      )}

      {/* Email Configuration Modal */}
      <Modal
        title="Send Email"
        open={emailModalVisible}
        onOk={handleEmailSubmit}
        onCancel={() => {
          setEmailModalVisible(false);
          emailForm.resetFields();
          setCurrentEmailAction(null);
        }}
        okText="Send Email"
        cancelText="Cancel"
        width={600}
      >
        <Form
          form={emailForm}
          layout="vertical"
          initialValues={{
            subject: `Analysis Results from ${activeAgent?.name || 'AI Assistant'}`,
            attachment_format: 'pdf',
            attach_full_report: true,
            attach_components: false
          }}
        >
          <Form.Item
            label="Recipient Email"
            name="to_email"
            rules={[
              { required: true, message: 'Please enter recipient email' },
              { type: 'email', message: 'Please enter a valid email address' }
            ]}
          >
            <Input placeholder="recipient@example.com" />
          </Form.Item>
          
          <Form.Item
            label="Subject"
            name="subject"
          >
            <Input placeholder="Email subject" />
          </Form.Item>
          
          <Form.Item
            label="Custom Message"
            name="custom_message"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="Add a personal message (optional)" 
            />
          </Form.Item>
          
          <Form.Item
            label="Attachment Format"
            name="attachment_format"
          >
            <Select>
              <Option value="pdf">PDF Document</Option>
              <Option value="docx">Word Document</Option>
              <Option value="html">HTML Report</Option>
              <Option value="json">JSON Data</Option>
            </Select>
          </Form.Item>
          
          <Form.Item name="attach_full_report" valuePropName="checked">
            <Checkbox>Attach complete analysis report</Checkbox>
          </Form.Item>
          
          <Form.Item name="attach_components" valuePropName="checked">
            <Checkbox>Attach individual components</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
      {/* Download Modal */}
      <Modal
        title="Download Ready"
        open={downloadModalVisible}
        onCancel={() => {
          setDownloadModalVisible(false);
          setDownloadUrl('');
          setDownloadFilename('');
        }}
        footer={[
          <Button key="close" onClick={() => {
            setDownloadModalVisible(false);
            setDownloadUrl('');
            setDownloadFilename('');
          }}>
            Close
          </Button>,
          <Button key="download" type="primary" href={downloadUrl} target="_blank">
            Download File
          </Button>
        ]}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <DownloadOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: '16px' }} />
          <p style={{ fontSize: '16px', marginBottom: '8px' }}>
            Your PDF file is ready for download:
          </p>
          <p style={{ fontWeight: 'bold', marginBottom: '16px' }}>
            {downloadFilename}
          </p>
          <p style={{ color: '#666', fontSize: '14px' }}>
            Click the "Download File" button below to save the file to your computer.
          </p>
        </div>
      </Modal>

      <div style={{ 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px 16px',
        background: '#f0f2f5',
        borderBottom: '1px solid #e8e8e8',
        marginBottom: '16px',
        borderRadius: '4px'
      }}>
        {/* Outcome text on the left */}
        <Title level={4} style={{ margin: 0, color: '#000000' }}>
          {outcomeText}
        </Title>
        
        {/* Actions on the right */}
        {(actions.length > 0 || loadingActions) && (
          <div>
            <FloatButton.Group
              trigger="hover"
              style={{ 
                position: 'relative', 
                bottom: 0, 
                right: 0
              }}
              shape="circle"
              icon={<ToolTwoTone twoToneColor="#6200EE" />}
              placement="left"
            >
              {loadingActions ? (
                <Tooltip title="Loading actions..." placement="bottom">
                  <FloatButton
                    icon={<CheckOutlined />}
                    loading={true}
                    style={{ borderColor: '#6200EE', color: '#6200EE' }}
                  />
                </Tooltip>
              ) : (
                actions.map((action, index) => (
                  <Tooltip
                    key={`action-${action.id || index}`}
                    title={action.name || action.button_name}
                    placement="bottom"
                  >
                    <FloatButton
                      icon={getIconComponent(action.icon)}
                      onClick={() => handleActionClick(action)}
                      loading={loadingStates[action.endpoint]}
                      style={{ borderColor: '#6200EE', color: '#6200EE' }}
                    />
                  </Tooltip>
                ))
              )}
            </FloatButton.Group>
          </div>
        )}
      </div>
    </>
  );
};

export default HeaderActionCenter;